/**
 * Canvas da operação: caixas HTML (para caberem selects) sobre um SVG com as
 * setas. Cada seta carrega o nº da NF que o motor fiscal atribuiu à perna, então
 * o desenho e o roteiro nunca saem de sincronia.
 */

import { LayoutGrid, Link2, Presentation, RotateCcw, Trash2, X } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { OperacaoControle } from "@/hooks/useOperacao";
import { exportarFluxoPpt, ID_CANVAS_FLUXO } from "@/lib/exportarPpt";
import { cn } from "@/lib/utils";
import type { GrupoNo, No, ResultadoOperacao, Seta, Terminal, TipoNo } from "@/types";

const LARGURA_NO = 184;
const ALTURA_NO = 82;
const LARGURA_CANVAS = 1040;
/*
 * 470 em vez dos 560 iniciais: o nó mais baixo dos modelos fica em y=340 e a
 * caixa tem 82 de altura, então tudo termina em 422 — o resto era faixa vazia
 * no pé do cartão. `posicaoLivre` (useOperacao) respeita o mesmo teto.
 */
const ALTURA_CANVAS = 470;

/**
 * O desenho vive num sistema de coordenadas fixo — as posições dos nós vêm dos
 * modelos do backend, montados para 1040x560. Para acompanhar a largura do card
 * sem remexer nesse layout, o canvas inteiro é redimensionado por
 * `transform: scale`: a geometria é a mesma, só em outro tamanho.
 *
 * `FATOR_LARGURA` é o quanto da largura disponível o diagrama toma. Agora que a
 * casca do app tem exatamente a largura nativa do desenho (1040px de conteúdo),
 * ele ocupa a faixa inteira e o teto fica em 1: nunca ampliamos além do tamanho
 * de origem. O piso segura o extremo oposto, em telas estreitas.
 */
const FONTE_ROTULO = '700 12px Montserrat, "Segoe UI", system-ui, sans-serif';
const FONTE_ICMS = '400 11px Montserrat, "Segoe UI", system-ui, sans-serif';

/**
 * Largura real do texto, medida no mesmo tipo em que ele será desenhado.
 * Contar caracteres não serve: Montserrat é larga e "Remessa por conta e ordem"
 * estoura o balão muito antes de um rótulo do mesmo tamanho em números.
 */
const medirTexto = (() => {
  let ctx: CanvasRenderingContext2D | null | undefined;
  return (texto: string, fonte: string): number => {
    if (ctx === undefined) ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return texto.length * 6.8; // sem canvas: estimativa conservadora
    ctx.font = fonte;
    return ctx.measureText(texto).width;
  };
})();

/* Anatomia do balão, da esquerda para a direita: recuo, disco do número, texto,
   × de remover, recuo. Só o texto é variável — o resto é fixo. */
const PAD_BALAO = 11;
const R_DISCO = 10.5;
const GAP_DISCO = 7;
const R_BOTAO = 10.5;
const GAP_BOTAO = 9;
const LARGURA_BALAO_MIN = 132;
/**
 * Alvo da coluna de texto: passando disso, o rótulo quebra em duas linhas em
 * vez de esticar o balão — um balão largo demais atravessa o desenho e cobre
 * setas vizinhas, enquanto uma segunda linha custa 16px de altura.
 */
const LARGURA_TEXTO_ALVO = 150;
/** Teto de segurança: nem com duas linhas o balão vira uma faixa no canvas. */
const LARGURA_BALAO_MAX = 260;
const ALTURA_BALAO_1 = 34;
const ALTURA_BALAO_2 = 50;

interface MetricasBalao {
  largura: number;
  altura: number;
  discoCx: number;
  textoX: number;
  larguraTexto: number;
  botaoCx: number;
  /** Linhas do rótulo já quebradas, com a linha de base de cada uma. */
  linhas: Array<{ texto: string; y: number }>;
  ySub: number;
}

/**
 * Quebra o rótulo em duas linhas no ponto que deixa as duas mais parecidas —
 * é o corte que rende o balão mais estreito. Uma palavra só, ou um texto que
 * já cabe, continua em linha única.
 */
function quebrarRotulo(texto: string, largura: number, fonte: string): string[] {
  if (medirTexto(texto, fonte) <= largura) return [texto];
  const palavras = texto.split(/\s+/).filter(Boolean);
  if (palavras.length < 2) return [texto];

  let melhor: { linhas: string[]; custo: number } | null = null;
  for (let i = 1; i < palavras.length; i++) {
    const a = palavras.slice(0, i).join(" ");
    const b = palavras.slice(i).join(" ");
    const custo = Math.max(medirTexto(a, fonte), medirTexto(b, fonte));
    if (!melhor || custo < melhor.custo) melhor = { linhas: [a, b], custo };
  }
  return melhor!.linhas;
}

/**
 * Dimensiona o balão a partir do texto que ele precisa mostrar, em vez de
 * cortar o texto para caber numa caixa fixa: a largura é a da maior linha
 * (rótulo ou ICMS) mais as partes fixas.
 */
function metricasBalao(
  rotulo: string,
  sub: string,
  temNumero: boolean,
  temBotao: boolean,
): MetricasBalao {
  const fixo =
    PAD_BALAO * 2 +
    (temNumero ? R_DISCO * 2 + GAP_DISCO : 0) +
    (temBotao ? GAP_BOTAO + R_BOTAO * 2 : 0);

  const linhasRotulo = quebrarRotulo(rotulo, LARGURA_TEXTO_ALVO, FONTE_ROTULO);
  const texto = Math.max(
    ...linhasRotulo.map((linha) => medirTexto(linha, FONTE_ROTULO)),
    medirTexto(sub, FONTE_ICMS),
  );

  const largura = Math.min(
    LARGURA_BALAO_MAX,
    Math.max(LARGURA_BALAO_MIN, Math.ceil(fixo + texto)),
  );
  const duasLinhas = linhasRotulo.length > 1;
  const esquerda = -largura / 2;
  const textoX = esquerda + PAD_BALAO + (temNumero ? R_DISCO * 2 + GAP_DISCO : 0);
  const botaoCx = largura / 2 - PAD_BALAO - R_BOTAO;

  return {
    largura,
    altura: duasLinhas ? ALTURA_BALAO_2 : ALTURA_BALAO_1,
    discoCx: esquerda + PAD_BALAO + R_DISCO,
    textoX,
    larguraTexto: (temBotao ? botaoCx - R_BOTAO - GAP_BOTAO : largura / 2 - PAD_BALAO) - textoX,
    botaoCx,
    // Linhas de base medidas do centro do balão: rótulo(s) em cima, ICMS embaixo.
    linhas: duasLinhas
      ? [
          { texto: linhasRotulo[0], y: -9 },
          { texto: linhasRotulo[1], y: 3.5 },
        ]
      : [{ texto: linhasRotulo[0], y: -2.5 }],
    ySub: duasLinhas ? 16 : 11.5,
  };
}

/**
 * Texto secundário do balão — o mesmo em todos os pontos que precisam medi-lo.
 * O tratamento vem prefixado com o tributo: "ICMS 17,00%", "ICMS diferido", "ICMS R$ 1,57/l".
 */
const subDaSeta = (seta: Seta) => {
  if (!seta.icms) return seta.auto ? "automática" : "sem NF";
  const valor = /^[A-ZÀ-Ú][a-zà-ú]/.test(seta.icms)
    ? seta.icms.charAt(0).toLowerCase() + seta.icms.slice(1)
    : seta.icms;
  return `ICMS ${valor}`;
};

/** Rede de segurança para o teto de largura: fora dele, nada é cortado. */
function encurtar(texto: string, largura: number, fonte: string): string {
  if (medirTexto(texto, fonte) <= largura) return texto;
  let corte = texto.length;
  while (corte > 1 && medirTexto(`${texto.slice(0, corte).trimEnd()}…`, fonte) > largura) {
    corte -= 1;
  }
  return `${texto.slice(0, corte).trimEnd()}…`;
}

const FATOR_LARGURA = 1;
const ESCALA_MIN = 0.8;
/*
 * Teto em 1: ampliar o desenho junto com a faixa engordava caixas e fontes e
 * quebrava a proporção que os 1040px nativos têm com a caixa de 184x82.
 */
const ESCALA_MAX = 1;

interface Props {
  controle: OperacaoControle;
  resultado?: ResultadoOperacao;
  ufs: string[];
  nomesUfs?: Record<string, string>;
  terminais: Terminal[];
  tipoVisual: Record<TipoNo, { cor: string; nome: string }>;
  somenteLeitura?: boolean;
}

interface Ponto {
  x: number;
  y: number;
}

/** Liga as bordas das duas caixas, e não os centros — a seta encosta na borda. */
function ancoras(de: No, para: No): { inicio: Ponto; fim: Ponto } {
  const deX = typeof de.x === "number" && !isNaN(de.x) ? de.x : 40;
  const deY = typeof de.y === "number" && !isNaN(de.y) ? de.y : 220;
  const paraX = typeof para.x === "number" && !isNaN(para.x) ? para.x : 560;
  const paraY = typeof para.y === "number" && !isNaN(para.y) ? para.y : 220;
  const c1 = { x: deX + LARGURA_NO / 2, y: deY + ALTURA_NO / 2 };
  const c2 = { x: paraX + LARGURA_NO / 2, y: paraY + ALTURA_NO / 2 };
  const dx = c2.x - c1.x;
  const dy = c2.y - c1.y;
  const distancia = Math.hypot(dx, dy) || 1;
  const recuar = (centro: Ponto, sinal: number): Ponto => {
    const escalaX = LARGURA_NO / 2 / (Math.abs(dx) / distancia || 1e-6);
    const escalaY = ALTURA_NO / 2 / (Math.abs(dy) / distancia || 1e-6);
    const t = Math.min(escalaX, escalaY);
    return { x: centro.x + (sinal * dx * t) / distancia, y: centro.y + (sinal * dy * t) / distancia };
  };
  return { inicio: recuar(c1, 1), fim: recuar(c2, -1) };
}

interface GeometriaSeta {
  seta: Seta;
  de: No;
  para: No;
  pathD: string;
  balaoX: number;
  balaoY: number;
  cor: string;
  /** Largura sob medida deste balão — usada no desenho e no antichoque. */
  metricas: MetricasBalao;
  /** Posição calculada, quando o usuário arrastou o balão para longe dela. */
  ancora?: Ponto;
}

/**
 * Calcula os traçados das setas e a posição dos balões para que múltiplas
 * ligações (ex.: ida e volta, ou paralelas) nunca sobreponham suas setas nem
 * empilhem seus balões informativos.
 */
function calcularGeometriasSetas(
  setas: Seta[],
  porId: Map<number, No>,
  podeRemover: boolean,
): GeometriaSeta[] {
  const metricasDe = (seta: Seta) =>
    metricasBalao(
      seta.rotulo,
      subDaSeta(seta),
      seta.n !== null,
      // Mesmo teste do <Aresta>: sem edgeId não há o que remover, e o × não entra.
      podeRemover && !seta.auto && !!seta.edgeId,
    );

  const mapaPares = new Map<string, Seta[]>();
  for (const seta of setas) {
    const de = porId.get(seta.de);
    const para = porId.get(seta.para);
    if (!de || !para) continue;
    const chave = `${Math.min(de.id, para.id)}-${Math.max(de.id, para.id)}`;
    const lista = mapaPares.get(chave) ?? [];
    lista.push(seta);
    mapaPares.set(chave, lista);
  }

  const itens: GeometriaSeta[] = [];

  for (const [, grupo] of mapaPares) {
    const total = grupo.length;
    const primeiro = grupo[0];
    const noMin = porId.get(Math.min(primeiro.de, primeiro.para))!;
    const noMax = porId.get(Math.max(primeiro.de, primeiro.para))!;

    const c1 = {
      x: (typeof noMin.x === "number" ? noMin.x : 40) + LARGURA_NO / 2,
      y: (typeof noMin.y === "number" ? noMin.y : 220) + ALTURA_NO / 2,
    };
    const c2 = {
      x: (typeof noMax.x === "number" ? noMax.x : 560) + LARGURA_NO / 2,
      y: (typeof noMax.y === "number" ? noMax.y : 220) + ALTURA_NO / 2,
    };
    const dx = c2.x - c1.x;
    const dy = c2.y - c1.y;
    const dist = Math.hypot(dx, dy) || 1;
    const ux = dx / dist;
    const uy = dy / dist;
    // Vetor normal unitário apontando à esquerda do vetor c1 -> c2
    const nx = -uy;
    const ny = ux;

    grupo.forEach((seta, i) => {
      const de = porId.get(seta.de)!;
      const para = porId.get(seta.para)!;
      const cor = seta.auto ? "#8A927F" : seta.modo === "simbolica" ? "#0E7490" : "#1B6B43";
      const { inicio, fim } = ancoras(de, para);

      if (total === 1) {
        const meio = { x: (inicio.x + fim.x) / 2, y: (inicio.y + fim.y) / 2 };
        itens.push({
          seta,
          de,
          para,
          pathD: `M ${inicio.x.toFixed(1)} ${inicio.y.toFixed(1)} L ${fim.x.toFixed(1)} ${fim.y.toFixed(1)}`,
          balaoX: Math.round(meio.x),
          balaoY: Math.round(meio.y),
          cor,
          metricas: metricasDe(seta),
        });
      } else {
        const offsetIndex = i - (total - 1) / 2;
        const ehVertical = Math.abs(uy) > 0.7;

        // Curvatura suave para separar as setas
        const curvaturaBase = ehVertical ? 38 : 50;
        const h = offsetIndex * curvaturaBase * (total === 2 ? 1.8 : 1.4);

        const meioLinear = { x: (inicio.x + fim.x) / 2, y: (inicio.y + fim.y) / 2 };
        const ctrl = {
          x: meioLinear.x + nx * h,
          y: meioLinear.y + ny * h,
        };

        // Posição ao longo do arco (t): em linhas verticais, a largura do balão exige escalonamento
        let t = 0.5;
        if (ehVertical && total >= 2) {
          t = i === 0 ? 0.34 : 0.66;
          if (total > 2) {
            t = 0.25 + (0.5 * i) / (total - 1);
          }
        }

        // Posição no Bézier quadrático: B(t) = (1-t)^2 P0 + 2(1-t)t P1 + t^2 P2
        const bx = (1 - t) * (1 - t) * inicio.x + 2 * (1 - t) * t * ctrl.x + t * t * fim.x;
        const by = (1 - t) * (1 - t) * inicio.y + 2 * (1 - t) * t * ctrl.y + t * t * fim.y;

        let finalBalaoY = by;
        let finalBalaoX = bx;
        if (!ehVertical && Math.abs(ny) > 0.4) {
          finalBalaoY += offsetIndex * 38;
        }

        itens.push({
          seta,
          de,
          para,
          pathD: `M ${inicio.x.toFixed(1)} ${inicio.y.toFixed(1)} Q ${ctrl.x.toFixed(1)} ${ctrl.y.toFixed(1)} ${fim.x.toFixed(1)} ${fim.y.toFixed(1)}`,
          balaoX: Math.round(finalBalaoX),
          balaoY: Math.round(finalBalaoY),
          cor,
          metricas: metricasDe(seta),
        });
      }
    });
  }

  // Relaxamento contra sobreposição entre quaisquer balões na tela
  const GAP_X = 12;
  const GAP_Y = 8;

  for (let iter = 0; iter < 6; iter++) {
    let houveColisao = false;
    for (let i = 0; i < itens.length; i++) {
      for (let j = i + 1; j < itens.length; j++) {
        const a = itens[i];
        const b = itens[j];
        const dx = Math.abs(a.balaoX - b.balaoX);
        const dy = Math.abs(a.balaoY - b.balaoY);
        // Balões de larguras diferentes: a distância mínima é a média das duas.
        const minX = (a.metricas.largura + b.metricas.largura) / 2 + GAP_X;
        const minY = (a.metricas.altura + b.metricas.altura) / 2 + GAP_Y;

        if (dx < minX && dy < minY) {
          houveColisao = true;
          const sobX = minX - dx;
          const sobY = minY - dy;

          if (sobY <= sobX) {
            const desloc = sobY / 2 + 1;
            if (a.balaoY <= b.balaoY) {
              a.balaoY -= desloc;
              b.balaoY += desloc;
            } else {
              a.balaoY += desloc;
              b.balaoY -= desloc;
            }
          } else {
            const desloc = sobX / 2 + 1;
            if (a.balaoX <= b.balaoX) {
              a.balaoX -= desloc;
              b.balaoX += desloc;
            } else {
              a.balaoX += desloc;
              b.balaoX -= desloc;
            }
          }
        }
      }
    }
    if (!houveColisao) break;
  }

  // Garante que permaneçam na área visível
  for (const item of itens) {
    const meia = item.metricas.largura / 2;
    item.balaoX = Math.max(meia + 8, Math.min(LARGURA_CANVAS - meia - 8, item.balaoX));
    const meiaAltura = item.metricas.altura / 2;
    item.balaoY = Math.max(
      meiaAltura + 8,
      Math.min(ALTURA_CANVAS - meiaAltura - 8, item.balaoY),
    );
  }

  return itens;
}

function Aresta({
  geometria,
  onRemover,
  onArrastarBalao,
  somenteLeitura,
}: {
  geometria: GeometriaSeta;
  onRemover?: () => void;
  onArrastarBalao?: (evento: React.PointerEvent) => void;
  somenteLeitura?: boolean;
}) {
  const { seta, pathD, balaoX, balaoY, cor, metricas, ancora } = geometria;
  const { largura, altura, discoCx, textoX, larguraTexto, botaoCx, linhas, ySub } = metricas;
  const sub = subDaSeta(seta);
  // O mesmo teste usado no cálculo da largura — o × só existe em perna manual.
  const temX = !somenteLeitura && !!onRemover && !seta.auto;

  return (
    <g>
      <path
        d={pathD}
        fill="none"
        stroke={cor}
        strokeWidth={2.2}
        strokeDasharray={seta.modo === "simbolica" ? "8 5" : undefined}
        markerEnd={`url(#seta-${seta.auto ? "auto" : seta.modo})`}
      />
      {/* Balão arrastado: um fio liga-o de volta ao ponto da seta a que pertence. */}
      {ancora && (
        <>
          <line
            x1={ancora.x}
            y1={ancora.y}
            x2={balaoX}
            y2={balaoY}
            stroke={cor}
            strokeWidth={1}
            strokeDasharray="3 3"
            opacity={0.7}
          />
          <circle cx={ancora.x} cy={ancora.y} r={3} fill={cor} />
        </>
      )}
      <g
        transform={`translate(${balaoX}, ${balaoY})`}
        className={onArrastarBalao && !somenteLeitura ? "cursor-grab active:cursor-grabbing" : undefined}
        onPointerDown={somenteLeitura ? undefined : onArrastarBalao}
      >
        <rect
          x={-largura / 2}
          y={-altura / 2}
          width={largura}
          height={altura}
          rx={8}
          fill="#fff"
          stroke={cor}
        />
        {seta.n !== null && (
          <>
            <circle cx={discoCx} cy={0} r={R_DISCO} fill={cor} />
            <text x={discoCx} y={4.5} textAnchor="middle" fontSize={13} fontWeight={800} fill="#fff">
              {seta.n}
            </text>
          </>
        )}
        {linhas.map((linha, i) => (
          <text
            key={i}
            x={textoX}
            y={linha.y}
            fontSize={12}
            fontWeight={700}
            fill="#0F3325"
          >
            {i === 0 && <title>{seta.rotulo}</title>}
            {encurtar(linha.texto, larguraTexto, FONTE_ROTULO)}
          </text>
        ))}
        <text x={textoX} y={ySub} fontSize={11} fill="#5B6357">
          {encurtar(sub, larguraTexto, FONTE_ICMS)}
        </text>
        {temX && (
          <g
            className="cursor-pointer"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onRemover}
            role="button"
            aria-label="Remover ligação"
            data-so-tela
          >
            <circle cx={botaoCx} cy={0} r={R_BOTAO} fill="#F2F3EE" stroke="#C8CCBF" />
            <text x={botaoCx} y={5} textAnchor="middle" fontSize={14} fill="#B4322B">
              ×
            </text>
          </g>
        )}
      </g>
    </g>
  );
}

function CaixaNo({
  no,
  visual,
  ufs,
  nomesUfs,
  terminais,
  selecionado,
  modoLigar,
  somenteLeitura,
  onArrastar,
  onClicar,
  onAtualizar,
  onRemover,
}: {
  no: No;
  visual: { cor: string; nome: string };
  ufs: string[];
  nomesUfs?: Record<string, string>;
  terminais: Terminal[];
  selecionado: boolean;
  modoLigar: boolean;
  somenteLeitura?: boolean;
  onArrastar: (evento: React.PointerEvent, id: number) => void;
  onClicar: () => void;
  onAtualizar: (campos: Partial<No>) => void;
  onRemover: () => void;
}) {
  const ehArmazem = no.tipo === "ARMAZEM";
  const ehExterior = no.tipo === "EXTERIOR";
  // Produtor e cliente: do grupo FS (intercompany) ou terceiro — muda as contas do roteiro contábil.
  const temGrupo = no.tipo === "PRODUTOR" || no.tipo === "DISTRIBUIDORA";
  const grupo: GrupoNo = no.grupo ?? (no.tipo === "PRODUTOR" ? "fs" : "terceiro");

  // Segmentação dos armazéns por UF
  const terminaisPorUf = React.useMemo(() => {
    const mapa = new Map<string, Terminal[]>();
    for (const t of terminais) {
      const lista = mapa.get(t.uf) ?? [];
      lista.push(t);
      mapa.set(t.uf, lista);
    }
    for (const [, lista] of mapa) {
      lista.sort((a, b) => a.nome.localeCompare(b.nome));
    }
    return mapa;
  }, [terminais]);

  const terminaisUfAtual = no.uf ? (terminaisPorUf.get(no.uf) ?? []) : [];
  const outrasUfsComTerminais = Array.from(terminaisPorUf.keys())
    .filter((uf) => uf !== no.uf)
    .sort();

  return (
    <div
      className={cn(
        "absolute rounded-xl border bg-card shadow-media transition-all duration-200 ease-suave",
        selecionado
          ? "shadow-alta ring-2 ring-fs-menta ring-offset-2 ring-offset-background"
          : "hover:-translate-y-0.5 hover:shadow-alta",
        modoLigar && "cursor-crosshair",
      )}
      style={{
        left: typeof no.x === "number" && !isNaN(no.x) ? no.x : 40,
        top: typeof no.y === "number" && !isNaN(no.y) ? no.y : 220,
        width: LARGURA_NO,
        borderColor: visual.cor,
      }}
      onClick={modoLigar ? onClicar : undefined}
    >
      {/*
        O × é posicionado no canto e o cabeçalho reserva o espaço dele no
        `padding-right`: como fluxo, ele dividia a linha com o rótulo, e um nome
        de duas linhas ("DISTRIBUIDORA FS", "CLIENTE EXTERIOR") passava por baixo
        do botão a partir da segunda linha.
      */}
      <div
        className={cn(
          "relative rounded-t-[11px] py-1.5 pl-2.5 text-xs font-extrabold uppercase leading-tight tracking-[0.06em] text-white",
          somenteLeitura ? "pr-2.5" : "pr-7",
        )}
        style={{ background: visual.cor, cursor: somenteLeitura || modoLigar ? "default" : "grab" }}
        onPointerDown={somenteLeitura || modoLigar ? undefined : (e) => onArrastar(e, no.id)}
      >
        <span className="break-words">{visual.nome}</span>
        {!somenteLeitura && (
          <button
            type="button"
            aria-label="Remover nó"
            data-so-tela
            className="absolute right-1 top-1 rounded p-0.5 hover:bg-black/20"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onRemover();
            }}
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      <div className="space-y-1.5 p-2">
        {ehExterior ? (
          <div className="rounded-md border bg-secondary px-2 py-1.5 text-center text-sm font-bold">
            EXTERIOR
          </div>
        ) : (
          <Select
            value={no.uf}
            disabled={somenteLeitura}
            onValueChange={(uf) => {
              if (ehArmazem && no.terminal) {
                const termAtual = terminais.find((t) => t.nome === no.terminal);
                if (termAtual && termAtual.uf !== uf) {
                  onAtualizar({ uf, terminal: null, local: null });
                  return;
                }
              }
              onAtualizar({ uf });
            }}
          >
            {/*
              O texto do item é o mesmo nó React nos dois lugares (o Radix o
              reaproveita dentro do gatilho). Na lista queremos "SP · São Paulo";
              no gatilho, só a sigla — daí esconder o nome do estado apenas
              quando ele é renderizado dentro do gatilho, em vez de cortá-lo.
            */}
            <SelectTrigger className="h-8 px-2.5 text-sm font-bold [&_[data-nome-uf]]:hidden">
              <SelectValue placeholder="UF" />
            </SelectTrigger>
            <SelectContent className="max-h-80 w-[15rem] overflow-y-auto">
              {ufs.map((uf) => (
                <SelectItem key={uf} value={uf}>
                  <span className="font-bold">{uf}</span>
                  {nomesUfs?.[uf] && (
                    <span data-nome-uf className="ml-1.5 text-xs font-normal text-muted-foreground">
                      · {nomesUfs[uf]}
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {temGrupo && (
          <Select
            value={grupo}
            disabled={somenteLeitura}
            onValueChange={(valor) => onAtualizar({ grupo: valor as GrupoNo })}
          >
            <SelectTrigger className="h-8 px-2.5 text-xs font-semibold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fs">Grupo FS (intercompany)</SelectItem>
              <SelectItem value="terceiro">Terceiro</SelectItem>
            </SelectContent>
          </Select>
        )}

        {ehArmazem && (
          <Select
            value={no.terminal ?? "__livre"}
            disabled={somenteLeitura}
            onValueChange={(valor) => {
              if (valor === "__livre") {
                onAtualizar({ terminal: null, local: null });
                return;
              }
              const terminal = terminais.find((t) => t.nome === valor);
              if (terminal)
                onAtualizar({ terminal: terminal.nome, local: terminal.local, uf: terminal.uf });
            }}
          >
            <SelectTrigger className="h-auto min-h-8 whitespace-normal px-2.5 py-1.5 text-left text-xs font-semibold leading-tight [&>span]:overflow-visible [&>span]:whitespace-normal">
              <SelectValue placeholder="Terminal / Armazém" />
            </SelectTrigger>
            <SelectContent className="max-h-80 w-[20rem] overflow-y-auto">
              <SelectItem value="__livre">
                Armazém genérico ({no.uf})
              </SelectItem>

              {terminaisUfAtual.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="font-extrabold text-primary">
                    Terminais em {no.uf} ({nomesUfs?.[no.uf] ?? no.uf})
                  </SelectLabel>
                  {terminaisUfAtual.map((t) => (
                    <SelectItem key={t.nome} value={t.nome}>
                      {t.nome}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}

              {outrasUfsComTerminais.map((uf) => {
                const lista = terminaisPorUf.get(uf) ?? [];
                return (
                  <SelectGroup key={uf}>
                    <SelectLabel className="font-semibold text-muted-foreground">
                      {uf} — {nomesUfs?.[uf] ?? uf}
                    </SelectLabel>
                    {lista.map((t) => (
                      <SelectItem key={t.nome} value={t.nome}>
                        {t.nome} · {t.local}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                );
              })}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}

export function FlowCanvas({
  controle,
  resultado,
  ufs,
  nomesUfs,
  terminais,
  tipoVisual,
  somenteLeitura,
}: Props) {
  const { estado, modoLigar, ligandoDe, atualizarNo, removerNo, removerAresta, clicarNo, moverBalao } =
    controle;
  const molduraRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const arrasto = React.useRef<{ id: number; dx: number; dy: number } | null>(null);
  /** Balão em arrasto: ponteiro e deslocamento no início, para somar o movimento. */
  const arrastoBalao = React.useRef<{
    chave: string;
    x0: number;
    y0: number;
    dx0: number;
    dy0: number;
  } | null>(null);

  const [escala, setEscala] = React.useState(ESCALA_MIN);
  const escalaRef = React.useRef(1);
  escalaRef.current = escala;

  React.useEffect(() => {
    const moldura = molduraRef.current;
    if (!moldura) return;
    const medir = () => {
      const disponivel = moldura.clientWidth;
      if (!disponivel) return;
      const ajustada = (FATOR_LARGURA * disponivel) / LARGURA_CANVAS;
      setEscala(Math.min(ESCALA_MAX, Math.max(ESCALA_MIN, ajustada)));
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(moldura);
    return () => observador.disconnect();
  }, []);

  const porId = React.useMemo(
    () => new Map(estado.nodes.map((n) => [n.id, n])),
    [estado.nodes],
  );

  // Sem resultado ainda (primeira carga), desenha as arestas cruas.
  const setas: Seta[] = React.useMemo(() => {
    if (resultado?.setas.length) return resultado.setas;
    return estado.edges.map((e) => ({
      key: `e${e.id}`,
      de: e.de,
      para: e.para,
      modo: "fisica" as const,
      auto: false,
      n: null,
      rotulo: "—",
      icms: null,
      edgeId: e.id,
    }));
  }, [resultado, estado.edges]);

  /*
    Montserrat chega por webfont: uma medição feita antes disso usa a métrica da
    fonte de recuo e erra o corte. `fontesProntas` só existe para forçar um
    redesenho quando a fonte real termina de carregar.
  */
  const [fontesProntas, setFontesProntas] = React.useState(false);
  React.useEffect(() => {
    let vivo = true;
    document.fonts?.ready.then(() => vivo && setFontesProntas(true));
    return () => {
      vivo = false;
    };
  }, []);

  const geometriasCalculadas = React.useMemo(
    () => calcularGeometriasSetas(setas, porId, !somenteLeitura),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fontesProntas força o recorte dos rótulos a refazer
    [setas, porId, somenteLeitura, fontesProntas],
  );

  /*
    O deslocamento que o usuário deu ao balão é somado depois do antichoque:
    ele manda na posição final. "Ordenar" zera os deslocamentos.
  */
  const geometrias = React.useMemo(() => {
    const baloes = estado.baloes ?? {};
    return geometriasCalculadas.map((geo) => {
      const desloc = baloes[geo.seta.key];
      if (!desloc || (!desloc.dx && !desloc.dy)) return geo;
      const meia = geo.metricas.largura / 2;
      const meiaAltura = geo.metricas.altura / 2;
      return {
        ...geo,
        ancora: { x: geo.balaoX, y: geo.balaoY },
        balaoX: Math.max(meia + 8, Math.min(LARGURA_CANVAS - meia - 8, geo.balaoX + desloc.dx)),
        balaoY: Math.max(
          meiaAltura + 8,
          Math.min(ALTURA_CANVAS - meiaAltura - 8, geo.balaoY + desloc.dy),
        ),
      };
    });
  }, [geometriasCalculadas, estado.baloes]);

  const aoMover = React.useCallback(
    (evento: PointerEvent) => {
      const balao = arrastoBalao.current;
      if (balao) {
        const k = escalaRef.current;
        moverBalao(
          balao.chave,
          Math.round(balao.dx0 + (evento.clientX - balao.x0) / k),
          Math.round(balao.dy0 + (evento.clientY - balao.y0) / k),
        );
        return;
      }
      const atual = arrasto.current;
      const caixa = containerRef.current?.getBoundingClientRect();
      if (!atual || !caixa) return;
      const k = escalaRef.current;
      const x = Math.max(
        0,
        Math.min(LARGURA_CANVAS - LARGURA_NO, (evento.clientX - caixa.left) / k - atual.dx),
      );
      const y = Math.max(
        0,
        Math.min(ALTURA_CANVAS - ALTURA_NO, (evento.clientY - caixa.top) / k - atual.dy),
      );
      atualizarNo(atual.id, { x, y });
    },
    [atualizarNo, moverBalao],
  );

  React.useEffect(() => {
    const soltar = () => {
      arrasto.current = null;
      arrastoBalao.current = null;
    };
    window.addEventListener("pointermove", aoMover);
    window.addEventListener("pointerup", soltar);
    return () => {
      window.removeEventListener("pointermove", aoMover);
      window.removeEventListener("pointerup", soltar);
    };
  }, [aoMover]);

  const iniciarArrastoBalao = (evento: React.PointerEvent, geo: GeometriaSeta) => {
    if (modoLigar) return;
    evento.preventDefault();
    /*
      Parte do deslocamento que está na tela (já limitado às bordas), e não do
      guardado: senão um balão empurrado contra a borda "prende" o arrasto de volta.
    */
    const base = geo.ancora ?? { x: geo.balaoX, y: geo.balaoY };
    arrastoBalao.current = {
      chave: geo.seta.key,
      x0: evento.clientX,
      y0: evento.clientY,
      dx0: geo.balaoX - base.x,
      dy0: geo.balaoY - base.y,
    };
  };

  const iniciarArrasto = (evento: React.PointerEvent, id: number) => {
    const no = porId.get(id);
    const caixa = containerRef.current?.getBoundingClientRect();
    if (!no || !caixa) return;
    const k = escalaRef.current;
    const noX = typeof no.x === "number" && !isNaN(no.x) ? no.x : 40;
    const noY = typeof no.y === "number" && !isNaN(no.y) ? no.y : 220;
    arrasto.current = {
      id,
      dx: (evento.clientX - caixa.left) / k - noX,
      dy: (evento.clientY - caixa.top) / k - noY,
    };
  };

  return (
    <div
      ref={molduraRef}
      className="w-full overflow-x-auto bg-[linear-gradient(180deg,#FBFCFA,#F4F7F2)] py-3"
    >
      {/*
        `scale` não muda o tamanho que o elemento ocupa no layout — esta casca
        reserva o espaço já ampliado para o card não cortar o desenho.
      */}
      <div
        style={{ width: LARGURA_CANVAS * escala, height: ALTURA_CANVAS * escala }}
        className="canvas-casca relative mx-auto"
      >
        <div
          ref={containerRef}
          id={ID_CANVAS_FLUXO}
          className="canvas-escalado absolute left-0 top-0 origin-top-left select-none bg-[radial-gradient(circle,#D7DED2_1px,transparent_1px)] [background-size:24px_24px]"
          style={{ width: LARGURA_CANVAS, height: ALTURA_CANVAS, transform: `scale(${escala})` }}
        >
          <svg
            width={LARGURA_CANVAS}
            height={ALTURA_CANVAS}
            className="absolute inset-0"
            aria-label="Diagrama da operação"
          >
            <defs>
              {[
                ["fisica", "#1B6B43"],
                ["simbolica", "#0E7490"],
                ["auto", "#8A927F"],
              ].map(([nome, cor]) => (
                <marker
                  key={nome}
                  id={`seta-${nome}`}
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="8"
                  markerHeight="8"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={cor} />
                </marker>
              ))}
            </defs>
            {geometrias.map((geo) => (
              <Aresta
                key={geo.seta.key}
                geometria={geo}
                somenteLeitura={somenteLeitura}
                onRemover={geo.seta.edgeId ? () => removerAresta(geo.seta.edgeId!) : undefined}
                onArrastarBalao={(e) => iniciarArrastoBalao(e, geo)}
              />
            ))}
          </svg>

          {estado.nodes.map((no) => (
            <CaixaNo
              key={no.id}
              no={no}
              visual={tipoVisual[no.tipo] ?? { cor: "#55585C", nome: no.tipo }}
              ufs={ufs}
              nomesUfs={nomesUfs}
              terminais={terminais}
              selecionado={ligandoDe === no.id}
              modoLigar={modoLigar}
              somenteLeitura={somenteLeitura}
              onArrastar={iniciarArrasto}
              onClicar={() => clicarNo(no.id)}
              onAtualizar={(campos) => atualizarNo(no.id, campos)}
              onRemover={() => removerNo(no.id)}
            />
          ))}

          {modoLigar && (
            <Badge variant="info" className="absolute left-3 top-3 shadow-media">
              <Link2 className="mr-1 size-3.5" />
              {ligandoDe === null ? "clique na origem" : "clique no destino"}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

/** Paleta de papéis: a faixa acima do desenho, o que constrói o grafo. */
export function BarraFerramentas({
  controle,
  tipos,
  tipoVisual,
}: {
  controle: OperacaoControle;
  tipos: TipoNo[];
  tipoVisual: Record<TipoNo, { cor: string; nome: string }>;
}) {
  const { adicionarNo } = controle;
  return (
    <div className="nao-imprimir flex flex-wrap items-center gap-2 border-y border-border/70 bg-secondary/40 px-5 py-2.5">
      <span className="rotulo-seco pr-0.5">Adicionar</span>
      {tipos.map((tipo) => (
        <Button
          key={tipo}
          variant="outline"
          size="sm"
          className="bg-card"
          onClick={() => adicionarNo(tipo)}
        >
          <span
            className="size-2.5 rounded-full"
            style={{ background: tipoVisual[tipo]?.cor ?? "#55585C" }}
          />
          {tipoVisual[tipo]?.nome ?? tipo}
        </Button>
      ))}
    </div>
  );
}

/**
 * Ações sobre o grafo já montado — ficam abaixo do desenho, junto do resultado
 * sobre o qual agem, e no mesmo traje dos botões da paleta.
 */
export function AcoesCanvas({
  controle,
  onCarregarModelo,
}: {
  controle: OperacaoControle;
  onCarregarModelo: () => void;
}) {
  const { alternarModoLigar, modoLigar, estado, substituirGrafo, ordenarFluxo } = controle;
  const [exportando, setExportando] = React.useState(false);
  const exportar = async () => {
    setExportando(true);
    try {
      await exportarFluxoPpt(estado.nome);
    } catch {
      toast.error("Não foi possível gerar o PowerPoint.");
    } finally {
      setExportando(false);
    }
  };
  return (
    <div className="nao-imprimir flex flex-wrap items-center gap-2">
      <Button
        variant={modoLigar ? "default" : "outline"}
        size="sm"
        className={modoLigar ? undefined : "bg-card"}
        onClick={alternarModoLigar}
        disabled={estado.nodes.length < 2}
      >
        <Link2 />
        Ligar nós
      </Button>
      <Button variant="outline" size="sm" className="bg-card" onClick={onCarregarModelo}>
        <RotateCcw />
        Carregar modelo
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="bg-card"
        onClick={() => substituirGrafo([], [])}
        disabled={!estado.nodes.length}
      >
        <Trash2 />
        Limpar
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="bg-card"
        onClick={ordenarFluxo}
        disabled={!estado.nodes.length}
        title="Cada clique alterna a montagem: polígono, linha, grade e estágios — e devolve os balões das setas à posição padrão"
      >
        <LayoutGrid />
        Ordenar
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="bg-card"
        onClick={exportar}
        disabled={!estado.nodes.length || exportando}
        title="Exporta o gráfico do fluxo para um slide do PowerPoint (.pptx)"
      >
        <Presentation />
        {exportando ? "Gerando…" : "Exportar"}
      </Button>
    </div>
  );
}

/**
 * Legenda das setas — o traço cheio/tracejado e a cor são a única pista do que
 * cada perna significa; sem isso o desenho exige decorar o código de cores.
 */
export function LegendaSetas() {
  const itens: Array<[string, string, boolean]> = [
    ["Movimentação física", "#1B6B43", false],
    ["Venda simbólica", "#0E7490", true],
    ["Perna automática", "#8A927F", false],
  ];
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {itens.map(([rotulo, cor, tracejada]) => (
        <li key={rotulo} className="flex items-center gap-1.5">
          <svg width="20" height="8" aria-hidden className="shrink-0">
            <line
              x1="0"
              y1="4"
              x2="20"
              y2="4"
              stroke={cor}
              strokeWidth="2.2"
              strokeDasharray={tracejada ? "5 3" : undefined}
            />
          </svg>
          {rotulo}
        </li>
      ))}
    </ul>
  );
}
